import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { C_BTN, C_BTN_SECONDARY } from "./buttonMetrics";

/**
 * SIMS-8647 — the delete-confirmation-with-counts pattern.
 *
 * Hand-off §2 rule 5: every delete has TWO states — blocked with real counts, and allowed with
 * the consequence spelled out. §6: the destructive action is never distinguished by colour alone,
 * so the button says what it does.
 *
 * Design: modal L525-541 (480px), `delBlocked` L834 = programs > 0 || members > 0, titles L987.
 *
 * Dependencies are a LIST rather than two named fields, because this is the shared pattern:
 * SIMS-8592 blocks on the roles granting an action, SIMS-8593 on recorded visits and treatment
 * plans. A list also fixes the design's own defect — `delBody` (L989) is one interpolated string
 * emitting both counts unconditionally, so a members-only block reads "configured on 0 programs
 * and has 412 registered members", stating a zero dependency as if it were a reason. AC2 and AC3
 * each want only the count that applies; AC4 wants both together (TestRail C17521).
 *
 * COUNTS ONLY, never an identifier (C17516, and the C-4 discipline): this component accepts
 * numbers, so there is no code path by which a member's identifier could reach the screen.
 *
 * `count: null` means "this dependency exists but the count is not available" — the member count
 * needs the backend dependency endpoint (plan D-3), and rendering 0 would read as "no members"
 * and invite a deletion the service should refuse.
 */

export interface DeleteDependency {
	/** Discriminator for the caller; the dialog only renders `label` and `count`. */
	kind: string;
	/** Plural noun, e.g. "registered members". Singularised automatically when the count is 1. */
	label: string;
	/** null when the dependency is known to exist but its size is not available. */
	count: number | null;
	/**
	 * The dependants by name, when naming them is safe AND useful.
	 *
	 * SIMS-8762: Hi-Fi actions-19 names the granting roles — "REP_EXPORT is granted by 3 roles:
	 * Program Manager, Site Supervisor, Reporting Analyst" — because the recovery is to remove it
	 * from those roles, and a bare count leaves the administrator hunting for which ones.
	 *
	 * This does NOT loosen the counts-only discipline in the docblock above. That rule is about
	 * PEOPLE: a member, a visit, a participant. Roles are configuration, and TestRail C17630 draws
	 * exactly that line — "the granting roles are named" AND "no individual user, member, visit or
	 * participant identifier appears". Omit this field for any dependency that is people, and the
	 * dialog renders the count alone as before.
	 */
	names?: string[];
}

export interface ConfigDeleteDialogProps {
	open: boolean;
	recordName: string;
	/** e.g. "payer" — used in the title and the destructive button. */
	recordNoun: string;
	/** Empty means nothing depends on the record, so deletion is allowed. */
	dependencies: DeleteDependency[];
	onConfirm: () => void;
	onClose: () => void;
	isDeleting?: boolean;
	/** Overrides the default "set it to Inactive instead" suggestion. */
	blockedSuggestion?: string;
	/**
	 * Body copy for the ALLOWED state, when the default payer wording does not fit.
	 *
	 * SIMS-8762: actions-20 words it as a permanent deletion — "EVE_MOVE_TO will be permanently
	 * deleted. This cannot be undone. No roles grant this action." — which is a stronger and truer
	 * statement than "will be removed from the list and will no longer be available to select",
	 * because an action IS gone and the sentence has to say so.
	 */
	allowedBody?: string;
	/**
	 * SIMS-8647 — the dependency counts are still being fetched. The destructive action is withheld
	 * until they are known: offering Delete beside "checking..." invites a click the answer might
	 * have forbidden.
	 */
	isCheckingDependencies?: boolean;
	/**
	 * SIMS-8647 — the dependency check FAILED, so what depends on this record is unknown.
	 *
	 * Blocks the delete (decision 2026-08-31). Treating a failed check as "no dependencies" is
	 * precisely how a payer with hundreds of registered members would be offered for deletion, and
	 * the service would refuse it anyway — so the refusal is stated here, where the user is.
	 */
	dependenciesUnavailable?: boolean;
}

const joinClauses = (parts: string[]): string =>
	parts.length <= 1
		? (parts[0] ?? "")
		: `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

/** "3 programs" · "1 program" · "3 roles: A, B and C" · "registered members" when unknown. */
const describe = ({ label, count, names }: DeleteDependency): string => {
	if (count === null) {
		return label;
	}
	const noun = count === 1 && label.endsWith("s") ? label.slice(0, -1) : label;
	const head = `${count} ${noun}`;
	return names && names.length > 0 ? `${head}: ${joinClauses(names)}` : head;
};

const ConfigDeleteDialog = ({
	open,
	recordName,
	recordNoun,
	dependencies,
	onConfirm,
	onClose,
	isDeleting = false,
	blockedSuggestion,
	allowedBody,
	isCheckingDependencies = false,
	dependenciesUnavailable = false,
}: ConfigDeleteDialogProps) => {
	// Anything other than a confirmed empty dependency set withholds the delete.
	const blocked =
		dependencies.length > 0 ||
		dependenciesUnavailable ||
		isCheckingDependencies;

	// Design L544-563: fixed 480px (wider than the discard prompt's 440 — this one carries
	// dependency counts), 12px radius, and the design's own paddings: title 20px 24px 8px, body
	// 0 24px 16px, actions 12px 24px 20px with a 12px gap. MUI's defaults differ on all of them,
	// and `maxWidth="sm" + fullWidth` is ~600px rather than 480.
	return (
		<Dialog
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: "30rem",
					maxWidth: "100%",
					m: 2,
					borderRadius: "0.75rem",
				},
			}}
		>
			{/* 18px/700 (L547); MUI's DialogTitle is 20px/500. */}
			<DialogTitle
				sx={{
					padding: "1.25rem 1.5rem 0.5rem",
					fontSize: "1.125rem",
					fontWeight: 700,
					lineHeight: 1.5,
				}}
			>
				{isCheckingDependencies
					? `Checking what uses this ${recordNoun}…`
					: dependenciesUnavailable
						? `${recordNoun[0].toUpperCase()}${recordNoun.slice(1)} can't be deleted yet`
						: blocked
							? `${recordNoun[0].toUpperCase()}${recordNoun.slice(1)} can't be deleted`
							: `Delete ${recordNoun}?`}
			</DialogTitle>
			<DialogContent sx={{ padding: "0 1.5rem 1rem" }}>
				{isCheckingDependencies ? (
					<Typography sx={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}>
						{`Looking up the programs and members that use ${recordName}.`}
					</Typography>
				) : dependenciesUnavailable ? (
					<Typography sx={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}>
						{`What uses ${recordName} could not be checked just now, so it cannot be deleted safely. Try again in a moment — deleting without this check could remove a ${recordNoun} that programs or registered members still depend on.`}
					</Typography>
				) : dependencies.length > 0 ? (
					<>
						<Typography
							sx={{ fontSize: "0.875rem", lineHeight: "1.25rem", mb: 1 }}
						>
							{`${recordName} has ${joinClauses(dependencies.map(describe))} depending on it, so it can't be deleted.`}
						</Typography>
						<Typography
							color="text.secondary"
							sx={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
						>
							{blockedSuggestion ??
								"Set it to Inactive instead — that stops new registrations while everything already recorded stays intact."}
						</Typography>
					</>
				) : (
					<Typography sx={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}>
						{allowedBody ??
							`${recordName} will be removed from the ${recordNoun} list and will no longer be available to select. Nothing already recorded against it changes.`}
					</Typography>
				)}
			</DialogContent>
			<DialogActions sx={{ padding: "0.75rem 1.5rem 1.25rem", gap: 1.5 }}>
				{blocked ? (
					<Button variant="outlined" onClick={onClose} sx={C_BTN_SECONDARY}>
						Close
					</Button>
				) : (
					<>
						<Button variant="outlined" onClick={onClose} sx={C_BTN_SECONDARY}>
							Cancel
						</Button>
						{/* Says what it does: §6 forbids relying on colour to mark the destructive action. */}
						<Button
							color="error"
							variant="contained"
							onClick={onConfirm}
							disabled={isDeleting}
							sx={C_BTN}
						>
							{`Delete ${recordNoun}`}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default ConfigDeleteDialog;
